import { createContext, ReactNode, useContext, useMemo, useRef } from "react"
import { create, useStore } from "zustand"
import { createJSONStorage, persist } from "zustand/middleware"
import { UserRole } from "@/apis/bff"
import { getDefaultWaitingRoomFilters } from "@/modules/waiting-room/helpers/waiting-room-helper"
import { WaitingRoomAppointmentsInput } from "@/modules/waiting-room/services/__generated__/useSlotUpdatedSubscription.graphql"
import { Language } from "@/types/__generated__/union-types"

interface WaitingRoomFilters {
  filters: WaitingRoomAppointmentsInput
  setFilters: (filters: WaitingRoomAppointmentsInput) => void
  resetFilters: () => void
}

const createWaitingRoomFiltersStore = (role: UserRole, userName: string, doctorLanguages: Language[] | null) => {
  return create<WaitingRoomFilters>()(
    persist(
      (set) => ({
        filters: getDefaultWaitingRoomFilters(role, userName, doctorLanguages),
        setFilters: (filters) =>
          set((state) => {
            const newState = {
              ...state.filters,
              ...filters,
            }
            return { filters: newState }
          }),
        resetFilters: () => set({ filters: {} }),
      }),
      {
        name: "waitingRoomFilterStore",
        storage: createJSONStorage(() => sessionStorage),
      },
    ),
  )
}

type WaitingRoomFiltersStore = ReturnType<typeof createWaitingRoomFiltersStore>

const WaitingRoomFiltersContext = createContext<WaitingRoomFiltersStore | null>(null)

const WaitingRoomFiltersProvider = ({
  children,
  role,
  userName,
  doctorLanguages,
}: {
  children: ReactNode
  role: UserRole
  userName: string
  doctorLanguages: Language[] | null
}) => {
  const filtersStoreRef = useRef<WaitingRoomFiltersStore>(
    createWaitingRoomFiltersStore(role, userName, doctorLanguages),
  )
  const searchStoreRef = useRef<WaitingRoomSearchStore>(createWaitingRoomSearchStore())

  return (
    <WaitingRoomFiltersContext.Provider value={filtersStoreRef.current}>
      <WaitingRoomSearchContext.Provider value={searchStoreRef.current}>{children}</WaitingRoomSearchContext.Provider>
    </WaitingRoomFiltersContext.Provider>
  )
}

const useWaitingRoomFilters = () => {
  const filters = useWaitingRoomFiltersContext((state) => state.filters)

  return useMemo(() => {
    const hasFilters = Object.values(filters).some((value) => value)
    return { filters, hasFilters }
  }, [filters])
}

function useWaitingRoomFiltersContext<T>(selector: (state: WaitingRoomFilters) => T): T {
  const store = useContext(WaitingRoomFiltersContext)
  if (!store) throw new Error("Missing WaitingRoomFiltersContext.Provider in the tree")

  return useStore(store, selector)
}

function useWaitingRoomSearchContext<T>(selector: (state: WaitingRoomSearch) => T): T {
  const store = useContext(WaitingRoomSearchContext)
  if (!store) throw new Error("Missing WaitingRoomSearchContext.Provider in the tree")

  return useStore(store, selector)
}

interface WaitingRoomSearch {
  searchValue: string
  setSearchValue: (searchValue: string) => void
}

const createWaitingRoomSearchStore = () => {
  return create<WaitingRoomSearch>()((set) => ({
    searchValue: "",
    setSearchValue: (searchValue: string) => set({ searchValue }),
  }))
}

type WaitingRoomSearchStore = ReturnType<typeof createWaitingRoomSearchStore>
const WaitingRoomSearchContext = createContext<WaitingRoomSearchStore | null>(null)

export {
  createWaitingRoomFiltersStore,
  useWaitingRoomFilters,
  useWaitingRoomFiltersContext,
  WaitingRoomFiltersProvider,
  useWaitingRoomSearchContext,
}
